"use client";

import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";

import { useLang } from "@/hooks/use-lang";
import { useUpdateQuery } from "@/hooks/use-update-query";

import { ChartSkeletons } from "ui/widget/skeletons/Skeletons";
import { ToggleButtons } from "ui/widget/toggle-buttons";

import {
  getHoldingsData,
  getIndustryData,
  getPortfolioDistributionOptions,
} from "../../../data/portfolio-distribution";
import { useInvestorPortfolioHoldings } from "../../../services/get-investor-portfolio-holdings";
import { useInvestorPortfolioIndustry } from "../../../services/get-investor-portfolio-industry";

const PortfolioDistributionChart = dynamic(() =>
  import("./PortfolioDistributionChart").then(
    (mod) => mod.PortfolioDistributionChart
  )
);

export const PortfolioDistribution = ({ investor }: { investor: string }) => {
  const { t } = useLang();
  const { updateQuery } = useUpdateQuery();
  const options = getPortfolioDistributionOptions({ t });
  const searchParams = useSearchParams();
  const portfolioDistributionIndex = +(
    searchParams.get("portfolio-distribution-index") ?? 0
  );
  const option = options[portfolioDistributionIndex].value;

  const { data: industryData, isLoading: isLoadingIndustry } =
    useInvestorPortfolioIndustry({
      investor,
    });

  const { data: holdingsData, isLoading: isLoadingHoldings } =
    useInvestorPortfolioHoldings({
      investor,
      enabled: option === options[1].value,
    });

  const type = option === options[0].value ? "industry" : "holdings";
  const isLoading =
    (type === "industry" && isLoadingIndustry) ||
    (type === "holdings" && isLoadingHoldings);

  const rawData =
    type === "industry"
      ? getIndustryData({ t, data: industryData ?? [] })
      : getHoldingsData({ t, data: holdingsData ?? [] });

  return (
    <div className="flex flex-col gap-y-4">
      <ToggleButtons
        value={option}
        options={options}
        onChange={(value) => {
          const index = options.findIndex((o) => o.value === value);
          updateQuery({
            name: "portfolio-distribution-index",
            value: index.toString(),
          });
        }}
        className="w-fit"
        id="portfolio-distribution-category"
      />
      {isLoading ? (
        <ChartSkeletons className="h-[260px] pb-[100px] @lg:h-[460px]" />
      ) : (
        <PortfolioDistributionChart type={type} rawData={rawData} />
      )}
    </div>
  );
};
